// Per-page renderer for "governance" — extracted from reportBuilder.js.
// Signature: async (api, doc, ctx) => { y, aiInsights, aiRecommendations }
import autoTable from 'jspdf-autotable';
import { NAVY, fetchPageAI } from '../utils';
import { drawVerticalBarChart } from '../charts';

export async function renderGovernance(api, doc, ctx) {
  const { qs, filterDesc, cs, pw, page, filters } = ctx;
  let y = ctx.y;
  let aiInsights = [];
  let aiRecommendations = [];

  const res = await api.get('/governance/dashboard');
  const d = res.data;

  const pds = d.pds || {};
  const dup = d.duplicate_suppliers || {};
  const cls = d.classification || {};
  const fresh = d.data_freshness || {};
  const sec = d.security || {};
  const sv = d.supplier_verification || {};
  const audit = d.audit_activity || {};
  const ret = d.retention || {};

  const sectionLines = Object.entries(pds.sections || {}).map(([k, v]) => `- ${k}: ${v}%`).join('\n');
  const dataContext = `Governance Health: ${d.governance_health}%\nPDS Score: ${pds.score}% (${pds.tier})\nTotal Records: ${pds.total_records}\n\nPDS Sections:\n${sectionLines}\n\nDuplicate Suppliers: ${dup.duplicates_detected}/${dup.total_suppliers} (${dup.duplicate_rate_pct}%)\nClassification Coverage: ${cls.coverage_pct}%\nData Freshness: ${fresh.status} (${fresh.days_since_last_upload} days)\nMFA: ${sec.mfa_enabled_count}/${sec.total_users} (${sec.mfa_pct}%)\nSupplier Verified: ${sv.verified}/${sv.total_suppliers} (${sv.verified_pct}%)\nAudit Events (30d): ${audit.events_last_30d}\nRetention Policy: ${ret.policy_years} years`;

  try {
    const aiRes = await fetchPageAI(api, { page, dataContext, filterDesc, filters });
    aiInsights = aiRes.insights;
    aiRecommendations = aiRes.recommendations;
  } catch {}

  // Governance Health KPI
  autoTable(doc, {
    startY: y,
    head: [['Governance Health', 'PDS Score', 'PDS Tier', 'Total Records']],
    body: [[`${d.governance_health}%`, `${pds.score}%`, (pds.tier || '').replace(/_/g, ' '), String(pds.total_records)]],
    styles: { fontSize: 10, halign: 'center' },
    headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // PDS Section Scores chart
  const pdsEntries = Object.entries(pds.sections || {});
  if (pdsEntries.length > 0) {
    const chartData = pdsEntries.map(([k, v]) => ({ label: k, value: v }));
    y = drawVerticalBarChart(doc, 14, y, pw - 28, 75, chartData, { title: 'PDS Section Completeness (%)' });
  }

  // Key metrics table
  if (y > 200) { doc.addPage(); y = 15; }
  autoTable(doc, {
    startY: y,
    head: [['Metric', 'Value']],
    body: [
      ['Classification Coverage', `${cls.coverage_pct}%`],
      ['Duplicate Suppliers', `${dup.duplicates_detected} of ${dup.total_suppliers} (${dup.duplicate_rate_pct}%)`],
      ['Data Freshness', `${fresh.status} (${fresh.days_since_last_upload} days since upload)`],
      ['MFA Enabled', `${sec.mfa_enabled_count} of ${sec.total_users} users (${sec.mfa_pct}%)`],
      ['Locked Accounts', String(sec.locked_accounts)],
      ['Suppliers Verified', `${sv.verified} of ${sv.total_suppliers} (${sv.verified_pct}%)`],
      ['Suppliers Pending', String(sv.pending)],
      ['Suppliers Expired', String(sv.expired)],
      ['Audit Events (30 days)', String(audit.events_last_30d)],
      ['AI Calls (30 days)', String(audit.ai_calls_last_30d)],
      ['Retention Policy', `${ret.policy_years} years`],
      ['Oldest Record', ret.oldest_record_date || 'N/A'],
      ['Purge Scheduler', ret.purge_scheduler_active ? 'Active' : 'Inactive'],
    ],
    styles: { fontSize: 8 },
    headStyles: { fillColor: NAVY },
    margin: { left: 14, right: 14 },
  });
  y = doc.lastAutoTable.finalY + 8;

  return { y, aiInsights, aiRecommendations };
}
